import { Inject, Injectable } from '@angular/core';
import { DefaultValue, FieldName, MyQuotesStatusHeader, SortOrder } from '../../services/shared/shared-enums';
import { BillingAddressPayload, BillingAddressResponse, Dealership, EquipmentQuoteListResponse, MyQuotesGridListObj, MyQuotesPayload, MyQuotesState, StatusPayload, InvoiceStatusLabel } from './definitions';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { AnalyticsDataLayer, TrackFileDownload, TrackSearch } from '../../services/analytics/definitions';
import { Address, LocationAddress, PromoBannerApiResponse, PromoBannerData } from '../../services/shared/shared-interface';
import { SharedService } from '../../services/shared/shared.service';
import { Control, ControlType, Count, FieldType } from 'kinetic-angular';
import { nullCheckValue } from '../../services/shared/shared-constants';
import { SharedUtilService } from '../../services/shared/shared-util.service';
import { DateConverterService } from '../../services/date-converter/date-converter.service';
import { BehaviorSubject, concatMap, tap } from 'rxjs';
import { ApiResponse } from '../../definitions/api-response.interface';
import { MyQuotesFactory } from './my-quotes.factory';
import { LoggerService } from '../../services/logger/logger.service';
import { ApiService } from '../../services/api/api.service';
import { SharedFilterService } from '../../services/shared/shared-filter.service';
import { expEnvironmentRef } from '../../injection-tokens/environment.injection-token';
import { EnvironmentRef } from 'projects/tmhna-portal/src/environments/interfaces';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
	providedIn: 'root'
})
export class MyQuotesUtilService 
{
	private readonly state$: BehaviorSubject<MyQuotesState> = new BehaviorSubject<MyQuotesState>(MyQuotesFactory.build());
	billingAddressListState: BehaviorSubject<LocationAddress[]> = new BehaviorSubject<LocationAddress[]>(MyQuotesFactory.billingAddressData());
	billingAddressFilterControls: Control[] = [];
	aemBaseUrl: string = this.environmentRef.aemBaseUrl;
	bannerFlag: boolean = false;
	get state(): MyQuotesState 
	{
		return this.state$.value;
	}
	public updateState(value: MyQuotesState): void 
	{
		this.state$.next(value);
	}
	constructor(public analyticsService: AnalyticsService, public sharedService: SharedService, public sharedUtilService: SharedUtilService, public dateService: DateConverterService,
		public apiService: ApiService, private readonly loggerService: LoggerService, public sharedFilterService: SharedFilterService,  @Inject(expEnvironmentRef) readonly environmentRef: EnvironmentRef) { }

	getDefaultPayload(): MyQuotesPayload 
	{
		return {
			skip: DefaultValue.skip,
			limit: DefaultValue.limit,
			locations: [],
			searchValue: '',
			status: [],
			salesProspectAddress: [],
			sort: { field: 'creationDate', order: SortOrder.desc }
		};
	}
	trackSuccessByEvent(event: AnalyticsDataLayer['event'], link: string, search?: TrackSearch, download?: TrackFileDownload): void
	{
		const eventData: AnalyticsDataLayer = {
			event,
			link: {
				name: link
			}
		};
		if(download)
		{
			eventData['download'] = download;
		}
		if(search)
		{
			eventData['search'] = search;
		}
		this.analyticsService.trackByEvent(eventData);
	}
	trackErrorByEvent(event: AnalyticsDataLayer['event'], errorMessage: string, link?: string, search?: string): void
	{
		const eventData: AnalyticsDataLayer = {
			event,
			error: {
				message: errorMessage
			}
		};
		if(link)
		{
			eventData['link'] = { name: link };
		}
		if(search)
		{
			eventData['search'] = { keyword: search };
		}
		this.analyticsService.trackByEvent(eventData);
	}
	formatAddress(address: Address): string
	{
		address = this.sharedService.nullAddressHandler(address);
		return this.sharedService.formatAddress(address, true);
	}
	getMyQuotesStatusAmount(status: Count, data: EquipmentQuoteListResponse): number
	{
		switch (status.header)
		{
		case MyQuotesStatusHeader.readyForReview:
			status.amount = data.readyForReviewCount;
			break;
		case MyQuotesStatusHeader.quoteinProcess:
			status.amount = data.inProcessCount;
			break;
		case MyQuotesStatusHeader.quoteAccepted:
			status.amount = data.acceptedCount;
			break;
		case MyQuotesStatusHeader.ordered:
			status.amount = data.orderedCount;
			break;
		case MyQuotesStatusHeader.expired:
			status.amount = data.expiredCount;
			break;
		}
		return status.amount;
	}
	getBillingAddressFilterControls(controls: Control[], billingAddresses: LocationAddress[]): Control[] 
	{
		const selectedBillingAddress: Control[] = [];
		controls.forEach(address => 
		{
			if (address.value)
				selectedBillingAddress.push(address);
		});
		controls = this.getBillingAddressValues(billingAddresses);
		controls.forEach(address => 
		{
			selectedBillingAddress.forEach(element => 
			{
				if (element.id === address.id)
					address.setValue(true);
			});
		});
		return controls;
	}
	getBillingAddressValues(billingAddresses: LocationAddress[]): Control[]
	{
		const assignedBillingAddress: Control[] = [];
		billingAddresses?.forEach((element, index) =>
		{
			const address = {
				addressLine1: element.addressLine1,
				addressLine2: element.addressLine2,
				city: element.city,
				state: element.state,
				country: element.country,
				zipCode: element.postalCode
			};
			const data = { name: element.label || '', id: index.toString(), address  };
			assignedBillingAddress.push(this.sharedService.initialiseFilterControl(data.id, data.name, ControlType.checkbox, data.address));
		});
		return assignedBillingAddress;
	}
	getDealerShipDetail(dealerDetails: Dealership): string 
	{
		if(dealerDetails && !Object.values(dealerDetails).every(value => value === null))
		{
			const dealerName = this.sharedService.nullValueHandler(dealerDetails.dealerName);
			const formattedAddress = this.sharedService.formatAddress(dealerDetails, true);
			if(!dealerDetails.dealerName && formattedAddress)
			{
				return this.sharedService.nullValueHandler(formattedAddress);
			}
			else
			{
				const dealerShipDetails = formattedAddress ? dealerName + ',' + '\n' + formattedAddress : dealerName;
				return this.sharedService.nullValueHandler(dealerShipDetails);

			}
		}
		else 
		{
			return nullCheckValue;
		}
	}
	checkCategory(key: string): string
	{
		let selectedCat: string = FieldType.text;
		selectedCat = (key === FieldName.status) ? FieldType.myQuoteStatus : this.sharedUtilService.getFieldType(key);
		return selectedCat;
	}
	checkStatus(value: string): string
	{
		if(value === StatusPayload.quoteinProcess)
		{
			value = InvoiceStatusLabel.quoteinProcess;
		}
		if(value === StatusPayload.quoteAccepted)
		{
			value = InvoiceStatusLabel.quoteAccepted;
		}
		return value;
	}
	trackAnalytics(data: MyQuotesGridListObj[], searchData: string, totalCount: number)
	{
		if(data.length > 0)
		{
			const search: TrackSearch = {
				keyword: searchData,
				numberOfResults: totalCount,
				type: 'Equipment Quotes'
			};
			this.trackSuccessByEvent('search results', 'search results', search);
		}
		else
		{
			this.trackErrorByEvent('search results', 'No Results Found.', 'search results', searchData);
		}
	}
	checkForFilterApplied(myQuotesPayload: MyQuotesPayload): boolean
	{
		const totalStatusLength = Object.keys(StatusPayload).length;
		const hasValidSearchValue = myQuotesPayload.searchValue && myQuotesPayload.searchValue.length >= 4;
		const hasBillingAddress = myQuotesPayload.salesProspectAddress?.length;
		const hasMismatchedStatusLength = myQuotesPayload.status?.length !== totalStatusLength;
		const hasDateRangeFilterApplied = this.checkForDateRageFilterApplied(myQuotesPayload);
		if(hasValidSearchValue || hasBillingAddress || hasMismatchedStatusLength || hasDateRangeFilterApplied)
		{
			return true;
		}
		return false;
	}
	checkForDateRageFilterApplied(myQuotesPayload: MyQuotesPayload): boolean
	{
		let hasDateRangeFilterApplied = false;
		const currentDate = new Date();
		const defaultStartDate = new Date(this.dateService.getFromDate(new Date(), 60)).toISOString().substring(0, 10);
		const defaultEndDate = new Date(this.dateService.getUnixTimeStamp(currentDate)).toISOString().substring(0, 10);
		if(myQuotesPayload.startDate !==  defaultStartDate || myQuotesPayload.endDate !== defaultEndDate)
		{
			hasDateRangeFilterApplied = true;
		}
		return hasDateRangeFilterApplied;
	}
	getBillingAddress(): void
	{
		const payload: BillingAddressPayload = {
			locations: []
		};
		this.sharedService
			.getLoggedInUseLocationsIdsObs()
			.pipe(
				tap((locationIds: string[]) =>
				{
					payload.locations = locationIds;
				}),
				concatMap(() => this.apiService.getBillingAddress(payload))
			)
			.subscribe({
				next: (res: ApiResponse) =>
				{
					if (res?.data)
					{
						const data = <BillingAddressResponse>res.data;
						this.billingAddressListState.next(data.records);
					}
				},
				error: (error) =>
				{
					this.loggerService.error('error in my quotes search billing address call', error);
					throw error;
				}
			});
	}
	public async findAssignedBillingAddress(searchKey: string): Promise<Control[]> 
	{
		this.billingAddressFilterControls = this.getBillingAddressFilterControls(this.billingAddressFilterControls, this.billingAddressListState.value);

		return this.sharedFilterService.findSearchedAddress(searchKey, this.billingAddressFilterControls);
	}
	updateBillingAddressSection(sectionControls: Control[], appliedAddressIds: string[]): void
	{
		if (appliedAddressIds && appliedAddressIds.length > 0) 
		{			
			sectionControls.forEach((control) => 
			{
				if (control.type === ControlType.checkbox)
				{
					if(!appliedAddressIds.includes(control.id))
						control.setValue(false);
					else
						control.setValue(true);
				}
			});
		}
		else 
		{
			sectionControls.forEach((control: Control) => 
			{
				if (control.type === ControlType.checkbox)
					control.setValue(false);
			});
		}

	}
	getquotePromoBanner()
	{
		this.apiService.quoteContentBlock().subscribe({ next: (res) =>
		{
			if (res?.data)
			{
				const responseObj: PromoBannerApiResponse = <PromoBannerApiResponse>(res.data);
				this.handleSubscriptions(responseObj);
				this.bannerFlag = true;
			}
		},
		error: (error: HttpErrorResponse): void =>
		{
			if(error)
			{
				this.state.sectionBottom.sectionPromoBanner.subscriptionBanner.subscriptionsDesktop = [];
				this.state.sectionBottom.sectionPromoBanner.subscriptionBanner.subscriptionsMobile = [];
				this.bannerFlag = false;
			}
		}
		});
	}
	handleSubscriptions(res: PromoBannerApiResponse)
	{
		if(res.subscriptionBanner)
		{
			if(res.subscriptionBanner.subscriptionsDesktop.length > 0)
			{
					 res.subscriptionBanner.subscriptionsDesktop.forEach((item) =>
				{
					this.assignSubscriptionItems(item);
				});
			}
			this.state.sectionBottom.sectionPromoBanner.subscriptionBanner.subscriptionsDesktop = res.subscriptionBanner.subscriptionsDesktop? res.subscriptionBanner.subscriptionsDesktop: [];
			if(res.subscriptionBanner.subscriptionsMobile.length > 0)
			{
				res.subscriptionBanner.subscriptionsMobile.forEach( (item) =>
				{
					this.assignSubscriptionItems(item);
				});
			}
			this.state.sectionBottom.sectionPromoBanner.subscriptionBanner.subscriptionsMobile = res.subscriptionBanner.subscriptionsMobile? res.subscriptionBanner.subscriptionsMobile : [];
		}
	}
	
	assignSubscriptionItems(item : PromoBannerData)
	{
		if(item)
		{
			item.heading= item?.heading ? item?.heading : '';
			item.link.url= this.sharedService.nullValueHandler(item.link?.url);
			item.link.label= this.sharedService.nullValueHandler(item.link?.label);
			item.link.icon.iconPath= this.sharedService.nullValueHandler(item.link?.icon?.iconPath);
			item.link.icon.altText= this.sharedService.nullValueHandler(item.link?.icon?.altText);
			item.bannerImage= {
				iconPath: this.sharedService.nullValueHandler(this.aemBaseUrl + item.bannerImage?.iconPath),
				altText: this.sharedService.nullValueHandler(item.bannerImage?.altText)
			};
		}
	}

}
